import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useTier } from '../hooks/useTier';
import { useNotifications } from '../hooks/useNotifications';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getUserGroupInvitations } from '../services/groupChatInvitationService';
import { acceptChatRequest, declineChatRequest, deleteChat, leaveChat, archiveChat, unarchiveChat } from '../services/chatService';

/**
 * ChatContext - Global chat state management
 *
 * Provides:
 * - Chat list with real-time updates
 * - Selected chat state
 * - Unread counts (soft badges)
 * - Chat permissions based on tier
 */

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { user, userProfile } = useAuth();
  const { hasFeature, checkLimit, isTeam, hasOrganization, organizationId } = useTier();
  const { showMessageNotification, requestPermission, isGranted, isSupported } = useNotifications();

  // Chat state
  const [chats, setChats] = useState([]);
  const [archivedChats, setArchivedChats] = useState([]); // Archived chats (local only)
  const [pendingChats, setPendingChats] = useState([]); // Chat requests awaiting acceptance
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [groupInvitations, setGroupInvitations] = useState([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // UI state
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);

  // Track previous messages for notification detection
  const prevChatsRef = useRef(new Map());

  // Load user's chats from membership collection
  useEffect(() => {
    if (!user?.uid) {
      setChats([]);
      setLoading(false);
      return;
    }

    const membershipRef = collection(db, `chatMembership/${user.uid}/chats`);
    const q = query(membershipRef, orderBy('lastReadAt', 'desc'));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const chatMemberships = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch full chat data for each membership
      const chatPromises = chatMemberships.map(async (membership) => {
        try {
          const chatDoc = await getDoc(doc(db, 'chats', membership.chatId));
          if (chatDoc.exists()) {
            return {
              id: chatDoc.id,
              ...chatDoc.data(),
              membership: membership
            };
          }
          return null;
        } catch (error) {
          console.error('Error fetching chat:', error);
          return null;
        }
      });

      const chatData = await Promise.all(chatPromises);
      const validChats = chatData.filter(chat => chat !== null && chat.isActive !== false);

      // Separate archived and non-archived chats
      const archivedChatsList = validChats.filter(chat => chat.membership?.archived === true);
      const nonArchivedChats = validChats.filter(chat => !chat.membership?.archived);

      // Auto-unarchive: If an archived chat has a new message, move it back to active
      // This is the Apple-style UX - archived chats reappear when new messages arrive
      const prevChatsMap = prevChatsRef.current;
      for (const archivedChat of archivedChatsList) {
        const prevChat = prevChatsMap.get(archivedChat.id);
        const prevMessageTime = prevChat?.lastMessage?.timestamp?.toMillis?.() || 0;
        const newMessageTime = archivedChat.lastMessage?.timestamp?.toMillis?.() || 0;

        // New message from someone else - auto-unarchive
        if (
          newMessageTime > prevMessageTime &&
          archivedChat.lastMessage?.senderId !== user?.uid
        ) {
          // Unarchive in background (don't await to avoid blocking)
          unarchiveChat(archivedChat.id, user.uid).catch(console.error);
        }
      }

      // Separate active and pending chats based on membership status
      // IMPORTANT: Treat missing status as 'active' for backwards compatibility with existing chats
      const activeChats = nonArchivedChats.filter(chat =>
        !chat.membership?.status || chat.membership.status === 'active'
      );
      const pendingChatRequests = nonArchivedChats.filter(chat => chat.membership?.status === 'pending');

      // Compute hasUnread for each chat
      const chatsWithUnread = activeChats.map(chat => {
        const lastMessageTime = chat.lastMessage?.timestamp?.toMillis?.() || 0;
        const lastReadTime = chat.membership?.lastReadAt?.toMillis?.() || 0;
        const hasUnread = lastMessageTime > lastReadTime && chat.lastMessage?.senderId !== user?.uid;
        return { ...chat, hasUnread };
      });

      // Sort by last message time (unread first, then by time)
      chatsWithUnread.sort((a, b) => {
        // Unread chats first
        if (a.hasUnread && !b.hasUnread) return -1;
        if (!a.hasUnread && b.hasUnread) return 1;
        // Then by last message time
        const aTime = a.lastMessage?.timestamp?.toMillis?.() || 0;
        const bTime = b.lastMessage?.timestamp?.toMillis?.() || 0;
        return bTime - aTime;
      });

      pendingChatRequests.sort((a, b) => {
        const aTime = a.membership?.joinedAt?.toMillis?.() || 0;
        const bTime = b.membership?.joinedAt?.toMillis?.() || 0;
        return bTime - aTime;
      });

      // Calculate total unread count
      const totalUnread = chatsWithUnread.filter(c => c.hasUnread).length;
      setUnreadCounts({ total: totalUnread });

      // Check for new messages and show notifications
      chatsWithUnread.forEach(chat => {
        const prevChat = prevChatsRef.current.get(chat.id);
        const prevMessageTime = prevChat?.lastMessage?.timestamp?.toMillis?.() || 0;
        const newMessageTime = chat.lastMessage?.timestamp?.toMillis?.() || 0;

        // New message detected (not from current user, not currently viewing this chat)
        if (
          newMessageTime > prevMessageTime &&
          chat.lastMessage?.senderId !== user?.uid &&
          chat.id !== selectedChatId &&
          !document.hasFocus()
        ) {
          showMessageNotification(
            chat.lastMessage?.senderName || chat.name || 'New Message',
            chat.lastMessage?.text || 'You have a new message',
            chat.id,
            (chatId) => {
              setSelectedChatId(chatId);
              setIsChatOpen(true);
            }
          );
        }
      });

      // Update previous chats reference (include archived for new message detection)
      const newPrevChats = new Map();
      chatsWithUnread.forEach(chat => newPrevChats.set(chat.id, chat));
      archivedChatsList.forEach(chat => newPrevChats.set(chat.id, chat));
      prevChatsRef.current = newPrevChats;

      // Sort archived chats by last message time
      archivedChatsList.sort((a, b) => {
        const aTime = a.lastMessage?.timestamp?.toMillis?.() || 0;
        const bTime = b.lastMessage?.timestamp?.toMillis?.() || 0;
        return bTime - aTime;
      });

      setChats(chatsWithUnread);
      setArchivedChats(archivedChatsList);
      setPendingChats(pendingChatRequests);
      setLoading(false);
    }, (error) => {
      console.error('Error loading chat memberships:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Load selected chat details
  useEffect(() => {
    if (!selectedChatId) {
      setSelectedChat(null);
      return;
    }

    const chatRef = doc(db, 'chats', selectedChatId);
    const unsubscribe = onSnapshot(chatRef, (snapshot) => {
      if (snapshot.exists()) {
        setSelectedChat({ id: snapshot.id, ...snapshot.data() });
      } else {
        setSelectedChat(null);
        setSelectedChatId(null);
      }
    });

    return () => unsubscribe();
  }, [selectedChatId]);

  // Load group invitations
  useEffect(() => {
    if (!user?.uid) {
      setGroupInvitations([]);
      return;
    }

    const invitationsRef = collection(db, 'groupChatInvitations');
    const q = query(
      invitationsRef,
      where('invitedUserId', '==', user.uid),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const invitations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter out expired invitations
      const now = Date.now();
      const validInvitations = invitations.filter(inv => {
        if (inv.expiresAt) {
          return inv.expiresAt.toMillis() > now;
        }
        return true;
      });

      setGroupInvitations(validInvitations);
    }, (error) => {
      console.error('Error loading group invitations:', error);
      setGroupInvitations([]);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Refresh group invitations
  const refreshGroupInvitations = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const invitations = await getUserGroupInvitations(user.uid);
      setGroupInvitations(invitations);
    } catch (error) {
      console.error('Error refreshing group invitations:', error);
    }
  }, [user?.uid]);

  // Categorize chats by type
  const categorizedChats = useCallback(() => {
    const categories = {
      direct: [],
      meeting: [],
      group: [],
      organization: []
    };

    chats.forEach(chat => {
      switch (chat.type) {
        case 'dm':
          categories.direct.push(chat);
          break;
        case 'meeting':
          categories.meeting.push(chat);
          break;
        case 'group':
          if (hasFeature('groupChats')) {
            categories.group.push(chat);
          }
          break;
        case 'org_channel':
          if (isTeam && hasOrganization) {
            categories.organization.push(chat);
          }
          break;
        default:
          break;
      }
    });

    return categories;
  }, [chats, hasFeature, isTeam, hasOrganization]);

  // Check if user can create a new chat type
  const canCreateChat = useCallback((type) => {
    switch (type) {
      case 'dm':
        return hasFeature('directMessages');
      case 'group':
        if (!hasFeature('groupChats')) return false;
        const { allowed } = checkLimit('activeGroupChats', chats.filter(c => c.type === 'group').length);
        return allowed;
      case 'org_channel':
        return isTeam && hasOrganization;
      default:
        return false;
    }
  }, [hasFeature, checkLimit, chats, isTeam, hasOrganization]);

  // Select a chat
  const selectChat = useCallback((chatId) => {
    setSelectedChatId(chatId);
    setIsChatOpen(true);
  }, []);

  // Close chat panel
  const closeChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  // Open new chat modal
  const openNewChatModal = useCallback(() => {
    setShowNewChatModal(true);
  }, []);

  // Close new chat modal
  const closeNewChatModal = useCallback(() => {
    setShowNewChatModal(false);
  }, []);

  // Accept a chat request
  const acceptChat = useCallback(async (chatId) => {
    if (!user?.uid) return;

    try {
      await acceptChatRequest(user.uid, chatId);
      // Chat will automatically move from pending to active via the snapshot listener
    } catch (error) {
      console.error('Error accepting chat request:', error);
      throw error;
    }
  }, [user?.uid]);

  // Decline a chat request
  const declineChat = useCallback(async (chatId) => {
    if (!user?.uid) return;

    try {
      await declineChatRequest(user.uid, chatId);
      // Chat will be deleted from both users' chat lists
    } catch (error) {
      console.error('Error declining chat request:', error);
      throw error;
    }
  }, [user?.uid]);

  // Delete a chat (for DMs - removes for both users)
  const deleteChatConversation = useCallback(async (chatId) => {
    if (!user?.uid) return;

    try {
      await deleteChat(chatId, user.uid);
      // Clear selection if we deleted the currently selected chat
      if (selectedChatId === chatId) {
        setSelectedChatId(null);
        setSelectedChat(null);
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      throw error;
    }
  }, [user?.uid, selectedChatId]);

  // Leave a group chat
  const leaveChatGroup = useCallback(async (chatId) => {
    if (!user?.uid) return;

    try {
      await leaveChat(chatId, user.uid, userProfile?.displayName || 'User');
      // Clear selection if we left the currently selected chat
      if (selectedChatId === chatId) {
        setSelectedChatId(null);
        setSelectedChat(null);
      }
    } catch (error) {
      console.error('Error leaving chat:', error);
      throw error;
    }
  }, [user?.uid, userProfile?.displayName, selectedChatId]);

  // Archive a chat (local-only, Apple-style UX)
  // - Only hides for the current user
  // - Does not affect the other participant
  // - Auto-unarchives when new message arrives
  const archiveChatConversation = useCallback(async (chatId) => {
    if (!user?.uid) return;

    try {
      await archiveChat(chatId, user.uid);
      // Clear selection if we archived the currently selected chat
      if (selectedChatId === chatId) {
        setSelectedChatId(null);
        setSelectedChat(null);
      }
    } catch (error) {
      console.error('Error archiving chat:', error);
      throw error;
    }
  }, [user?.uid, selectedChatId]);

  // Unarchive a chat (restore to main list)
  const unarchiveChatConversation = useCallback(async (chatId) => {
    if (!user?.uid) return;

    try {
      await unarchiveChat(chatId, user.uid);
    } catch (error) {
      console.error('Error unarchiving chat:', error);
      throw error;
    }
  }, [user?.uid]);

  // Get chat display name (with DM name resolution via message data)
  const getChatDisplayName = useCallback((chat) => {
    if (!chat) return '';

    switch (chat.type) {
      case 'dm':
        // For DMs, use the last message's sender name from other participant
        // If chat has stored the other participant name, use that
        return chat.name || 'Direct Message';
      case 'meeting':
        return chat.name || 'Meeting Chat';
      case 'group':
        return chat.name || 'Group Chat';
      case 'org_channel':
        return `#${chat.channelSlug || chat.name}`;
      default:
        return chat.name || 'Chat';
    }
  }, [user?.uid]);

  // Get chat context label
  const getChatContextLabel = useCallback((chat) => {
    if (!chat) return '';

    switch (chat.type) {
      case 'dm':
        return '';
      case 'meeting':
        return 'Meeting Chat';
      case 'group':
        return 'Group';
      case 'org_channel':
        return 'Organization';
      default:
        return '';
    }
  }, []);

  // Enable notifications (request permission)
  const enableNotifications = useCallback(async () => {
    if (!isSupported) {
      console.warn('Notifications not supported');
      return false;
    }

    const granted = await requestPermission();
    setNotificationsEnabled(granted);
    return granted;
  }, [isSupported, requestPermission]);

  const value = {
    // State
    chats,
    archivedChats,
    archivedChatsCount: archivedChats.length,
    pendingChats,
    pendingChatsCount: pendingChats.length,
    selectedChatId,
    selectedChat,
    loading,
    unreadCounts,
    isChatOpen,
    showNewChatModal,

    // Computed
    categorizedChats: categorizedChats(),
    organizationId,

    // Actions
    selectChat,
    closeChat,
    openNewChatModal,
    closeNewChatModal,
    canCreateChat,
    getChatDisplayName,
    getChatContextLabel,
    acceptChat,
    declineChat,
    deleteChatConversation,
    leaveChatGroup,
    archiveChatConversation,
    unarchiveChatConversation,

    // Group invitations
    groupInvitations,
    groupInvitationsCount: groupInvitations.length,
    refreshGroupInvitations,

    // Notifications
    notificationsEnabled: isGranted,
    notificationsSupported: isSupported,
    enableNotifications,

    // Permissions
    canUseDMs: hasFeature('directMessages'),
    canUseGroupChats: hasFeature('groupChats'),
    canUseOrgChannels: isTeam && hasOrganization,
    canShareFiles: hasFeature('chatFileSharing'),
    canSearchChats: hasFeature('chatSearch'),
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

export default ChatContext;
